/**
 * The outbox model: a durable, ordered queue of workout writes (plan D18, D23).
 *
 * Everything here is PURE over an immutable {@link OutboxState}. Time comes in
 * as `now`, randomness as `random`, ids as `id` — so a test pins all three and
 * no timer is ever needed. The store (`store.ts`) persists these states and the
 * runner (`runner.ts`) drives them against the network.
 *
 * ── AT-LEAST-ONCE HERE, EXACTLY-ONCE ON THE SERVER ─────────────────────────
 * A crash after the server wrote but before `markDone` resends the item. That
 * is correct ONLY because the body carries the client-generated id: the server
 * answers a replay of an existing id with the stored row and creates nothing
 * (plan D19, reference api-contracts §1, T26/T27). This is why {@link enqueue}
 * refuses a creating write whose body has no `id` — without it, the resend is
 * a duplicate row.
 *
 * ── NOT ONLY THE WORKOUT SINCE `D194` ──────────────────────────────────────
 * The queue started as the workout's (`D163`) and now carries every write the
 * server answers idempotently: the profile's sections and the account's
 * settings (a `PUT` replaces), the plan's edits (client ids on the creating
 * ones, a second removal a 204), exclusions, measurements (an idempotency
 * key), the suggestion's dismissal, and the session's soreness and feedback.
 * {@link ACCOUNT_OUTBOX_KINDS} lists them; the second table below says what
 * `entityId` and `dependsOn` mean for each. The workout's kinds are
 * {@link WORKOUT_OUTBOX_KINDS}, and the store keeps the two apart on disk so an
 * older bundle — which knows only the workout's — never reads a kind it would
 * refuse (`store.ts`).
 *
 * ── WHAT `entityId`, `dependsOn` AND `accountId` MEAN, PER KIND ────────────
 * Together they are everything a sender needs besides the body:
 *
 * | kind            | entityId                         | dependsOn  | body.id              | accountId           |
 * |-----------------|----------------------------------|------------|----------------------|---------------------|
 * | `session.start` | the new session's id             | `null`     | = entityId, required | the signed-in account, or `null` |
 * | `session.end`   | the session's id (in the path)   | session id | —                    | idem                |
 * | `set.record`    | the new set's id                 | session id | = entityId, required | idem                |
 * | `set.correct`   | the corrected set's id (in path) | session id | replacement, required| idem                |
 * | `set.delete`    | the removed set's id (in path)   | session id | —                    | idem                |
 *
 * `dependsOn` is always the SESSION for every write but the start. That makes
 * the session the unit of ordering (its "stream"): every write of one session
 * is sent in the order it was enqueued, and different sessions never wait on
 * each other. See {@link nextReady}.
 *
 * The account's kinds (`D194`) follow the same rule — `dependsOn` names the
 * stream — with three shapes of stream:
 *
 * | kind                                   | entityId                    | dependsOn          | body                    |
 * |----------------------------------------|-----------------------------|--------------------|-------------------------|
 * | `session.soreness`, `session.feedback` | the session's id            | = entityId         | the contract input      |
 * | a section or setting `*.put`           | the kind itself             | = entityId         | the whole `PUT` body    |
 * | `plan.suggestion.dismiss`              | the kind itself             | = entityId         | —                       |
 * | `gym.use` (`D202`)                     | the kind itself             | = entityId         | `{ gymId }`             |
 * | `exclusion.add`, `exclusion.remove`    | the exercise id             | = entityId         | the contract input / —  |
 * | `exercise-note.put`, `.remove`         | the exercise id             | = entityId         | the contract input / —  |
 * | `exercise-preference.put`              | the exercise id             | = entityId         | the contract input      |
 * | `exercise-goal.add`                    | the goal's client id        | = entityId         | `id` = entityId         |
 * | `exercise-goal.remove`                 | the goal's client id        | = entityId         | —                       |
 * | `measurement.add`                      | the idempotency key         | = entityId         | `idempotencyKey` = id   |
 * | `measurement.delete`                   | the add's idempotency key   | = entityId         | `measurementId`, or none|
 * | `plan.item.add`                        | the new line's client id    | {@link PLAN_STREAM}| `id` = entityId         |
 * | `plan.item.replace`                    | the line being replaced     | {@link PLAN_STREAM}| `id` = the NEW line's   |
 * | `plan.item.remove`, `plan.item.restore`| the line                    | {@link PLAN_STREAM}| — / the contract input  |
 * | `plan.item.retarget`                   | the line being changed      | {@link PLAN_STREAM}| `id` = the NEW line's   |
 * | `plan.session.update`                  | the plan session's id       | {@link PLAN_STREAM}| the contract input      |
 * | `plan.session.add`                     | the new day's client id     | {@link PLAN_STREAM}| `id` = entityId         |
 * | `plan.session.remove`                  | the day                     | {@link PLAN_STREAM}| —                       |
 * | `plan.sessions.reorder` (`D210`)       | the plan                    | {@link PLAN_STREAM}| the contract input      |
 * | `plan.item.move` (`D214`)              | the line being moved        | {@link PLAN_STREAM}| `id` = the NEW line's   |
 * | `plan.superset` (`D246`)               | the first named line        | {@link PLAN_STREAM}| the contract input      |
 *
 * A soreness answer or a feedback rides its SESSION's stream, so it never
 * overtakes the start or the end it is about. Every plan write rides ONE
 * stream, because a reorder names the lines an add before it created and a
 * replace names the line an add created; per-line streams would let them
 * overtake each other. Every other write is its own stream: a second `PUT` of
 * the profile goes after the first, and nothing else waits on either.
 *
 * ── WHOSE WRITE IS IT (`accountId`, {@link OutboxScope}) ───────────────────
 * The queue is one per DEVICE, and a session that ends without the person
 * choosing it (a refused refresh) leaves items pending. Without an owner they
 * replay under whoever signs in next, and a `session.start` that never synced
 * carries no server row to refuse it: the server would create that workout
 * inside the new account. So every item names its account, and a reader or a
 * drain may be scoped to one. Three rules, and all three are load-bearing:
 *
 * - An item whose `accountId` is `null` — every item stored before this field
 *   existed, and any enqueued while no account was known — belongs to WHOEVER
 *   is in scope. Upgrading therefore strands no queued set.
 * - Omitting the scope means EVERY item. That is the behaviour before this
 *   field, so an unscoped caller is unchanged.
 * - A scope of `{ accountId: null }` matches nothing, `null` items included:
 *   no account signed in means nothing is sendable.
 *
 * Ordering ({@link isBlocked}) is deliberately account-BLIND: items of one
 * session always share one account, so this costs nothing, and it means no
 * filtering mistake can ever let an end overtake its own sets.
 *
 * ── NOTHING IS DROPPED SILENTLY (D23) ──────────────────────────────────────
 * The only removals are {@link markDone}, an explicit
 * {@link discardQuarantined}, {@link dropEntity} for a session the person
 * discarded locally before it ever synced, and {@link withdrawUnsent} for a
 * write the person undid. The last one removes NOTHING that could already be
 * on the server — it refuses the whole withdrawal unless every item of the
 * entity is certainly unsent — so D23 holds: the person's undo is the
 * decision, and a write that may have landed is deleted over the wire instead.
 */
/** The workout's writes (`D163`) — the only kinds a bundle before `D194`
 *  knows, and the only ones stored under the original key (`store.ts`). */
export const WORKOUT_OUTBOX_KINDS = [
    "session.start",
    "session.end",
    "set.record",
    "set.correct",
    "set.delete",
];
/** Everything else the outbox replays (`D194`). See the second table above. */
export const ACCOUNT_OUTBOX_KINDS = [
    "session.soreness",
    "session.feedback",
    "profile.put",
    "training-config.put",
    "equipment.put",
    "injuries.put",
    "parq.put",
    "notification-prefs.put",
    "me.locale.put",
    "me.display-name.put",
    "me.time-zone.put",
    "measurement.add",
    "measurement.delete",
    "exclusion.add",
    "exclusion.remove",
    // The person's own exercise data (`D200`): a note and a preference replace
    // by exercise; a goal is created under its client id and removed softly.
    "exercise-note.put",
    "exercise-note.remove",
    "exercise-preference.put",
    "exercise-goal.add",
    "exercise-goal.remove",
    "plan.suggestion.dismiss",
    "gym.use",
    "plan.session.update",
    "plan.item.add",
    "plan.item.replace",
    "plan.item.remove",
    "plan.item.restore",
    "plan.item.retarget",
    "plan.session.add",
    "plan.session.remove",
    "plan.sessions.reorder",
    "plan.item.move",
    // `D246`: a later kind, so under the account's key like every one since
    // `D194` — never the workout's, which an older bundle parses whole (`T-220`).
    "plan.superset",
    // `D266`: kilograms or pounds, a later kind, so under the account's key
    // (`T-220`). A `PUT` of one value: replaying it is the same write.
    "me.weight-unit.put",
];
export const OUTBOX_KINDS = [
    ...WORKOUT_OUTBOX_KINDS,
    ...ACCOUNT_OUTBOX_KINDS,
];
/** Whether a kind is one an older bundle knows (`store.ts` splits on it). */
export function isWorkoutKind(kind) {
    return WORKOUT_OUTBOX_KINDS.includes(kind);
}
/** The one stream every plan write rides, in enqueue order (`D194`). */
export const PLAN_STREAM = "plan";
/** The kinds whose stream is their own `entityId`. */
const SELF_STREAM_KINDS = new Set([
    "session.soreness",
    "session.feedback",
    "profile.put",
    "training-config.put",
    "equipment.put",
    "injuries.put",
    "parq.put",
    "notification-prefs.put",
    "me.locale.put",
    "me.display-name.put",
    "me.time-zone.put",
    "me.weight-unit.put",
    "measurement.add",
    "measurement.delete",
    "exclusion.add",
    "exclusion.remove",
    "exercise-note.put",
    "exercise-note.remove",
    "exercise-preference.put",
    "exercise-goal.add",
    "exercise-goal.remove",
    "plan.suggestion.dismiss",
    "gym.use",
]);
/** The kinds that ride {@link PLAN_STREAM}. */
const PLAN_KINDS = new Set([
    "plan.session.update",
    "plan.item.add",
    "plan.item.replace",
    "plan.item.remove",
    "plan.item.restore",
    "plan.item.retarget",
    "plan.session.add",
    "plan.session.remove",
    "plan.sessions.reorder",
    "plan.item.move",
    "plan.superset",
]);
export const OUTBOX_ITEM_STATES = [
    "pending",
    "in_flight",
    "quarantined",
];
export const EMPTY_OUTBOX = { items: [] };
function inScope(item, scope) {
    if (scope === undefined) {
        return true;
    }
    if (scope.accountId === null) {
        return false;
    }
    return item.accountId === null || item.accountId === scope.accountId;
}
/** The items of one account, in queue order. Unowned items are included. */
export function itemsOf(state, scope) {
    return state.items.filter((item) => inScope(item, scope));
}
/**
 * Thrown by {@link enqueue} for an entry that would not replay safely or would
 * escape ordering. A programming error in the caller, never a runtime state —
 * so it throws rather than quarantining.
 */
export class OutboxEntryError extends Error {
    constructor(message) {
        super(message);
        this.name = "OutboxEntryError";
    }
}
// ── Backoff ──────────────────────────────────────────────────────────────────
export const BACKOFF_BASE_MS = 1000;
/** No two attempts are ever further apart than this, unless the server asks. */
export const BACKOFF_CAP_MS = 60_000;
/** A `Retry-After` is honoured up to this; beyond it is not a gym's timescale. */
export const RETRY_AFTER_CAP_MS = 10 * 60_000;
/**
 * Delay before attempt `attempts + 1`, after `attempts` failures (>= 1).
 *
 * Exponential with "equal jitter": the ceiling doubles from 1 s to the 60 s
 * cap, and the delay is uniformly between half the ceiling and the ceiling. So
 * it always grows until the cap, never exceeds it, and many devices coming back
 * online together do not resend in lockstep. A server `Retry-After` raises the
 * delay (it is a floor, never lowered by jitter).
 */
export function backoffDelayMs(attempts, random = Math.random, retryAfterMs) {
    const exponent = Math.max(0, attempts - 1);
    // 2^6 s already exceeds the cap; clamping the exponent avoids Infinity.
    const ceiling = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** Math.min(exponent, 16));
    const jittered = ceiling / 2 + (ceiling / 2) * clampUnit(random());
    const floor = retryAfterMs === undefined
        ? 0
        : Math.min(Math.max(0, retryAfterMs), RETRY_AFTER_CAP_MS);
    return Math.round(Math.max(jittered, floor));
}
function clampUnit(value) {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.min(1, Math.max(0, value));
}
// ── Operations ───────────────────────────────────────────────────────────────
const CREATING_KINDS = new Set([
    "session.start",
    "set.record",
    "plan.item.add",
]);
function bodyId(body) {
    if (typeof body !== "object" || body === null) {
        return undefined;
    }
    return body.id;
}
/**
 * Refuse an entry that a replay would duplicate, or that would escape its
 * session's ordering. See the table at the top of this file.
 */
export function assertReplaySafe(entry) {
    if (!OUTBOX_KINDS.includes(entry.kind)) {
        throw new OutboxEntryError(`Unknown outbox kind: ${String(entry.kind)}`);
    }
    if (entry.entityId === "") {
        throw new OutboxEntryError(`${entry.kind}: entityId is empty`);
    }
    if (!isWorkoutKind(entry.kind)) {
        assertAccountReplaySafe(entry);
        return;
    }
    const id = bodyId(entry.body);
    if (CREATING_KINDS.has(entry.kind) && id !== entry.entityId) {
        throw new OutboxEntryError(`${entry.kind}: body.id must be the client-generated entityId, or a replay creates a duplicate row`);
    }
    if (entry.kind === "set.correct" &&
        (typeof id !== "string" || id === "" || id === entry.entityId)) {
        throw new OutboxEntryError("set.correct: body.id must be the replacement row's own client id, or a replay creates a second replacement");
    }
    if (entry.kind === "session.start") {
        if (entry.dependsOn !== null) {
            throw new OutboxEntryError("session.start: dependsOn must be null");
        }
        return;
    }
    if (entry.dependsOn === null || entry.dependsOn === "") {
        throw new OutboxEntryError(`${entry.kind}: dependsOn must be the session id, or the write escapes its session's ordering`);
    }
    if (entry.kind === "session.end" && entry.dependsOn !== entry.entityId) {
        throw new OutboxEntryError("session.end: dependsOn must be the session being ended");
    }
}
/** The account kinds that CREATE under a client id, and what a replay with
 *  another id would duplicate (`D194`, `D200`). */
const CLIENT_ID_CREATES = {
    "exercise-goal.add": "creates a second goal",
    "plan.item.add": "adds a second line",
    "plan.session.add": "adds a second day",
};
/** {@link assertReplaySafe} for the account's kinds (`D194`, table above). */
function assertAccountReplaySafe(entry) {
    const stream = SELF_STREAM_KINDS.has(entry.kind)
        ? entry.entityId
        : PLAN_STREAM;
    if (entry.dependsOn !== stream) {
        throw new OutboxEntryError(`${entry.kind}: dependsOn must be ${PLAN_KINDS.has(entry.kind) ? `"${PLAN_STREAM}"` : "the entityId"}, or the write escapes its stream's ordering`);
    }
    const id = bodyId(entry.body);
    const creates = CLIENT_ID_CREATES[entry.kind];
    if (creates !== undefined && id !== entry.entityId) {
        throw new OutboxEntryError(`${entry.kind}: body.id must be the client-generated entityId, or a replay ${creates}`);
    }
    if ((entry.kind === "plan.item.replace" ||
        entry.kind === "plan.item.retarget" ||
        entry.kind === "plan.item.move") &&
        (typeof id !== "string" || id === "" || id === entry.entityId)) {
        throw new OutboxEntryError(`${entry.kind}: body.id must be the NEW line's own client id, or a replay meets 404 for the line it already replaced`);
    }
    if (entry.kind === "gym.use") {
        assertGymUse(entry);
    }
    if (entry.kind === "measurement.add") {
        const key = typeof entry.body === "object" && entry.body !== null
            ? entry.body.idempotencyKey
            : undefined;
        if (key !== entry.entityId) {
            throw new OutboxEntryError("measurement.add: body.idempotencyKey must be the entityId, or a replay appends a twin");
        }
    }
}
/**
 * `gym.use` (`D202`): one stream for every switch, so two taps offline land
 * in the order they were made and the second one wins — the gym the person
 * is standing in.
 */
function assertGymUse(entry) {
    const gymId = typeof entry.body === "object" && entry.body !== null
        ? entry.body.gymId
        : undefined;
    if (entry.entityId !== "gym.use" ||
        typeof gymId !== "string" ||
        gymId === "") {
        throw new OutboxEntryError('gym.use: entityId must be "gym.use" and body.gymId the gym, or two switches escape their order');
    }
}
/**
 * Append an entry at the back of the queue. Throws {@link OutboxEntryError}
 * for an entry that is not replay-safe; never mutates `state`.
 */
export function enqueue(state, entry, meta) {
    assertReplaySafe(entry);
    if (state.items.some((queued) => queued.id === meta.id)) {
        throw new OutboxEntryError(`Duplicate outbox item id: ${meta.id}`);
    }
    const item = {
        accountId: entry.accountId ?? null,
        attempts: 0,
        body: entry.body,
        createdAt: meta.now.toISOString(),
        dependsOn: entry.dependsOn,
        entityId: entry.entityId,
        id: meta.id,
        kind: entry.kind,
        lastError: null,
        nextAttemptAt: entry.notBefore ?? null,
        ...(entry.preview === undefined ? {} : { preview: entry.preview }),
        state: "pending",
    };
    return { item, state: { items: [...state.items, item] } };
}
/**
 * {@link enqueue} folded over several entries, in order, into ONE new state —
 * so the store persists them in one write and a kill keeps all of them or
 * none (`T-196`). `ids[i]` is the queue id of `entries[i]`. Every entry goes
 * through {@link enqueue}'s own checks, against the queue as the earlier
 * entries of the batch left it; one that fails throws and appends nothing.
 * No entries returns `state` itself, which the store writes nothing for.
 */
export function enqueueMany(state, entries, meta) {
    if (meta.ids.length !== entries.length) {
        throw new OutboxEntryError(`enqueueMany: ${entries.length} entries but ${meta.ids.length} queue ids`);
    }
    const items = [];
    let next = state;
    for (const [index, entry] of entries.entries()) {
        const appended = enqueue(next, entry, {
            id: meta.ids[index],
            now: meta.now,
        });
        items.push(appended.item);
        next = appended.state;
    }
    return { items, state: next };
}
/** The ordering unit: the session a write belongs to. */
function streamOf(item) {
    return item.dependsOn ?? item.entityId;
}
/**
 * Is this item held back by one enqueued before it?
 *
 * - An earlier write of the SAME session, in any state, holds it: FIFO within
 *   a session. So a set never overtakes its session's start (the server would
 *   answer 404 for an unknown session, which quarantines), an end never
 *   overtakes that session's sets (the server's `endedAt >= last set` rule,
 *   plan D20), and a correction never overtakes the set it corrects.
 *   A quarantined write keeps holding its session until it is retried or
 *   discarded: order is preserved, and nothing behind it is sent into a
 *   state the server would refuse.
 * - An earlier item whose `entityId` is this item's `dependsOn` holds it too
 *   (the dependency has not synced). With `dependsOn` always being the
 *   session this is the same condition; it is kept explicit so the rule the
 *   task states is the rule the code runs.
 * - A START is held by any `abandoned` END of ANOTHER session that is still
 *   on its way (`D194`), queued before it or after: "end the other and start
 *   new" queues the end either before the start (the logbook) or after it
 *   (the readiness check, which starts at the tap and asks meanwhile). Sent
 *   first, the start would meet the open session and be refused
 *   `workout.session_already_open`. A refused end does not hold it — that
 *   end is the person's to decide on, and the start's own 409 then offers
 *   "end the other" in the quarantine as before (`D177`). No deadlock: the
 *   end waits only on its own session's stream.
 */
function isBlocked(items, index) {
    const item = items[index];
    if (item === undefined) {
        return true;
    }
    if (item.kind === "session.start" &&
        items.some((other, at) => at !== index &&
            other.entityId !== item.entityId &&
            isAbandonOnItsWay(other))) {
        return true;
    }
    const stream = streamOf(item);
    for (let i = 0; i < index; i += 1) {
        const earlier = items[i];
        if (earlier === undefined) {
            continue;
        }
        if (streamOf(earlier) === stream ||
            (item.dependsOn !== null && earlier.entityId === item.dependsOn)) {
            return true;
        }
    }
    return false;
}
function isAbandonOnItsWay(item) {
    return (item.kind === "session.end" &&
        item.state !== "quarantined" &&
        typeof item.body === "object" &&
        item.body !== null &&
        item.body.endReason === "abandoned");
}
/**
 * The first item, in FIFO order, that may be sent at `now`: pending, past its
 * backoff, and not held back by an earlier write of its session. `null` when
 * nothing is ready — see {@link nextAttemptAt} for when something will be.
 *
 * With a `scope`, only that account's items are candidates; the blocking rule
 * still reads every earlier item, so a scope never lets a write overtake one
 * it depends on.
 */
export function nextReady(state, now, scope) {
    const at = now.getTime();
    for (let index = 0; index < state.items.length; index += 1) {
        const item = state.items[index];
        if (item === undefined || item.state !== "pending") {
            continue;
        }
        if (!inScope(item, scope)) {
            continue;
        }
        if (item.nextAttemptAt !== null && Date.parse(item.nextAttemptAt) > at) {
            continue;
        }
        if (isBlocked(state.items, index)) {
            continue;
        }
        return item;
    }
    return null;
}
/**
 * The earliest backoff deadline among pending items that nothing earlier holds
 * back, or `null` when none is waiting on time. The network runner (T11)
 * schedules its next drain for this instant.
 */
export function nextAttemptAt(state, scope) {
    let earliest = null;
    for (let index = 0; index < state.items.length; index += 1) {
        const item = state.items[index];
        if (item === undefined ||
            item.state !== "pending" ||
            item.nextAttemptAt === null ||
            !inScope(item, scope) ||
            isBlocked(state.items, index)) {
            continue;
        }
        const at = Date.parse(item.nextAttemptAt);
        if (earliest === null || at < earliest) {
            earliest = at;
        }
    }
    return earliest === null ? null : new Date(earliest);
}
function replace(state, id, change) {
    let found = false;
    const items = state.items.map((item) => {
        if (item.id !== id) {
            return item;
        }
        found = true;
        return change(item);
    });
    return found ? { items } : state;
}
/** The runner is sending this item. Persisted, so a crash can be recognised. */
export function markInFlight(state, id) {
    return replace(state, id, (item) => item.state === "pending" ? { ...item, state: "in_flight" } : item);
}
/** The server accepted it (or replayed it): the only success removal. */
export function markDone(state, id) {
    const items = state.items.filter((item) => item.id !== id);
    return items.length === state.items.length ? state : { items };
}
/**
 * Apply the D23 decision for a failed send.
 * - `retry` → pending, one more attempt, `nextAttemptAt` after
 *   {@link backoffDelayMs}. There is no limit on attempts.
 * - `reauth` → pending, attempts and backoff UNCHANGED: the item is not at
 *   fault, the account's session is. It resends after the next sign-in.
 * - `quarantine` → quarantined, with the server's `messageKey`.
 */
export function markFailed(state, id, failure, now, random = Math.random) {
    return replace(state, id, (item) => {
        switch (failure.action) {
            case "retry": {
                const attempts = item.attempts + 1;
                const delay = backoffDelayMs(attempts, random, failure.retryAfterMs);
                return {
                    ...item,
                    attempts,
                    lastError: failure.error,
                    nextAttemptAt: new Date(now.getTime() + delay).toISOString(),
                    state: "pending",
                };
            }
            case "reauth":
                return { ...item, lastError: failure.error, state: "pending" };
            case "quarantine":
                return {
                    ...item,
                    attempts: item.attempts + 1,
                    lastError: failure.error,
                    nextAttemptAt: null,
                    state: "quarantined",
                };
            default:
                return item;
        }
    });
}
/** The person tapped "retry": back to pending, due immediately. */
export function retryQuarantined(state, id) {
    return replace(state, id, (item) => item.state === "quarantined"
        ? { ...item, nextAttemptAt: null, state: "pending" }
        : item);
}
/**
 * The person tapped "discard" on a quarantined item. The only removal of an
 * unsent item besides {@link dropEntity}. An item that is not quarantined is
 * left alone: a pending one has not failed, and discarding it would be the
 * silent drop D23 forbids.
 */
export function discardQuarantined(state, id) {
    const items = state.items.filter((item) => !(item.id === id && item.state === "quarantined"));
    return items.length === state.items.length ? state : { items };
}
/**
 * Remove every write of an entity: those whose `entityId` or `dependsOn` is it.
 * For a session that never synced and was discarded locally, that is its
 * start, its sets and its end. The app calls this explicitly; it is never
 * called by the queue itself.
 */
export function dropEntity(state, entityId) {
    const items = state.items.filter((item) => item.entityId !== entityId && item.dependsOn !== entityId);
    return items.length === state.items.length ? state : { items };
}
/**
 * Is this item certainly NOT on the server?
 *
 * - `quarantined`: the server refused it, so it wrote nothing.
 * - `pending` with `attempts === 0`: it was never sent. A `reauth` failure
 *   leaves `attempts` alone (see {@link markFailed}), and that is correct: a
 *   401 wrote nothing either.
 *
 * `in_flight` is never withdrawable, and neither is a `pending` item with
 * `attempts > 0`: a network error or a 5xx can arrive after the server already
 * wrote the row (a lost response), so that row may exist.
 */
function isWithdrawable(item) {
    if (item.state === "quarantined") {
        return true;
    }
    return item.state === "pending" && item.attempts === 0;
}
/**
 * Cancel every queued write of one entity, but only when ALL of them are
 * certainly unsent ({@link isWithdrawable}). All or nothing: one item that may
 * have reached the server leaves the whole queue untouched and answers
 * `withdrawn: false`, and the caller then undoes over the wire (a `DELETE`)
 * instead of forgetting a row the server holds.
 *
 * `entityIds[0]` is the ORIGIN — the creating write's entity, a set's first id
 * (`supersedes[0] ?? set.id`). The queue must still hold that CREATING write
 * (`set.record`, `session.start`). When it does not, it has already synced
 * ({@link markDone} removed it) and only a correction or a removal is left:
 * withdrawing those alone would leave the row on the server while the device
 * drops it. So nothing is withdrawn.
 *
 * The kind is part of that check, not just the id: a `set.correct` carries the
 * CORRECTED row's `entityId`, so "some item has the origin's entityId" is also
 * true when the record is gone and only its correction remains.
 *
 * The origin check lives HERE, inside the pure operation, because the store
 * runs it on its single write chain. A separate "is the record still queued?"
 * read would race {@link markDone} exactly as {@link dropEntity} races
 * {@link markInFlight}.
 */
export function withdrawUnsent(state, entityIds) {
    const [origin] = entityIds;
    if (origin === undefined) {
        return { state, withdrawn: false };
    }
    const wanted = new Set(entityIds);
    const matched = state.items.filter((item) => wanted.has(item.entityId));
    // The creating write's presence is also what makes `matched` non-empty, so
    // an `entityIds` that matches nothing at all lands here too.
    const created = matched.some((item) => item.entityId === origin && CREATING_KINDS.has(item.kind));
    if (!(created && matched.every(isWithdrawable))) {
        return { state, withdrawn: false };
    }
    return {
        state: { items: state.items.filter((item) => !wanted.has(item.entityId)) },
        withdrawn: true,
    };
}
/**
 * Let a HELD start go now (`D194`, {@link OutboxEntry.notBefore}): its
 * `nextAttemptAt` is cleared. Only a start never sent — pending, no attempt —
 * is touched; anything else is left as it is. Returns the same state when
 * nothing changed, so the store writes nothing.
 */
export function release(state, entityId) {
    let changed = false;
    const items = state.items.map((item) => {
        if (item.kind !== "session.start" ||
            item.entityId !== entityId ||
            item.state !== "pending" ||
            item.attempts !== 0 ||
            item.nextAttemptAt === null) {
            return item;
        }
        changed = true;
        return { ...item, nextAttemptAt: null };
    });
    return changed ? { items } : state;
}
/**
 * The network came back: every pending item is due NOW rather than at the end
 * of a backoff that was computed for a network that was down (up to 60 s).
 * `attempts` is untouched — the history of failures is what sizes the NEXT
 * backoff, and what {@link isWithdrawable} reads.
 *
 * A 429 is the exception: the server asked for that delay with `Retry-After`,
 * and {@link backoffDelayMs} treats it as a floor. Clearing it would resend
 * into the same rate limit. So is a HELD start (`D194`): an item never sent
 * whose `nextAttemptAt` is a hold, not a backoff, and only {@link release}
 * lets it go — a wake while the readiness check is still asking must not.
 *
 * Returns the same state object when nothing changed, so the store writes
 * nothing.
 */
export function resetBackoff(state) {
    let changed = false;
    const items = state.items.map((item) => {
        if (item.state !== "pending" ||
            item.nextAttemptAt === null ||
            item.attempts === 0 ||
            item.lastError?.status === 429) {
            return item;
        }
        changed = true;
        return { ...item, nextAttemptAt: null };
    });
    return changed ? { items } : state;
}
/**
 * Empty means NOTHING at all, quarantined items included: sign-out asks for
 * confirmation while this is false (D23), and a quarantined set is still the
 * person's data.
 */
export function isEmpty(state, scope) {
    return !state.items.some((item) => inScope(item, scope));
}
/** Items still on their way: pending or in flight. */
export function pendingCount(state, scope) {
    return state.items.filter((item) => item.state !== "quarantined" && inScope(item, scope)).length;
}
/** Items the person has to decide on, in queue order. */
export function quarantined(state, scope) {
    return state.items.filter((item) => item.state === "quarantined" && inScope(item, scope));
}
/**
 * Items left `in_flight` by a process that died mid-send go back to pending.
 * The send may or may not have reached the server; resending is harmless
 * because the body carries the client id (see the header). Applied on load.
 *
 * Each one also counts as ONE more attempt. The process died mid-send, so the
 * write may have reached the server — left at `attempts: 0` such an item would
 * read as "never sent" to {@link withdrawUnsent}, and a withdrawal would then
 * forget a row the server holds.
 */
export function recoverInFlight(state) {
    if (!state.items.some((item) => item.state === "in_flight")) {
        return state;
    }
    return {
        items: state.items.map((item) => item.state === "in_flight"
            ? { ...item, attempts: item.attempts + 1, state: "pending" }
            : item),
    };
}
//# sourceMappingURL=outbox.js.map